import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./index.scss";
import onImg from '../../assets/noImg.jpeg';
/**
 * ImageModal 图片预览组件（受控）
 *
 * Props:
 * - open: boolean           是否显示
 * - imgsList: string[]      图片地址列表
 * - initialIndex?: number   初始展示索引（可选，默认0）
 * - onClose?: () => void    关闭回调（可选）
 */
const ImageModal = ({ open, imgsList = [], initialIndex = 0, onClose }) => {
  const hasImages = Array.isArray(imgsList) && imgsList.length > 0;
  const safeInitial = useMemo(() => {
    if (!hasImages) return 0;
    const n = Number(initialIndex) || 0;
    return Math.min(Math.max(n, 0), imgsList.length - 1);
  }, [hasImages, initialIndex, imgsList.length]);

  const [current, setCurrent] = useState(safeInitial);
  const [scale, setScale] = useState(1); // 图片缩放比例

  // 当列表或open变化时重置索引
  useEffect(() => {
    if (open) setCurrent(safeInitial);
  }, [open, safeInitial]);

  // 打开时禁止滚动
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const close = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const toPrev = useCallback(() => {
    if (!hasImages) return;
    setCurrent((idx) => (idx - 1 + imgsList.length) % imgsList.length);
  }, [hasImages, imgsList.length]);

  const toNext = useCallback(() => {
    if (!hasImages) return;
    setCurrent((idx) => (idx + 1) % imgsList.length);
  }, [hasImages, imgsList.length]);

  const handleClick = useCallback(() => {
    setScale((prevScale) => (prevScale === 1 ? 2 : 1)); // 单击在1倍和2倍之间切换
  }, []);

  // 键盘事件
  useEffect(() => {
    setScale(1); // 切换图片时重置缩放
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        toPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        toNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close, toPrev, toNext]);

  // 确保缩略图在视窗内
  useEffect(() => {
    if (!open || !hasImages) return;

    const activeThumb = document.querySelector(
      ".image-modal__thumbwrap.is-active"
    );
    if (activeThumb) {
      const thumbsContainer = activeThumb.parentElement;
      const { offsetTop, offsetHeight } = activeThumb;
      const { scrollTop, clientHeight } = thumbsContainer;

      if (
        offsetTop < scrollTop ||
        offsetTop + offsetHeight > scrollTop + clientHeight
      ) {
        thumbsContainer.scrollTop =
          offsetTop - clientHeight / 2 + offsetHeight / 2;
      }
    }
  }, [current, open, hasImages]);

  // 切换图片时重置图片父元素的 scrollTop
  useEffect(() => {
    const imgBox = document.querySelector('.image-modal__imgbox');
    if (imgBox) {
      imgBox.scrollTop = 0; // 切换图片后重置父元素的 scrollTop
    }
  }, [current]);

  if (!open) return null;

  return (
    <div className="image-modal" role="dialog" aria-modal="true">
      <div className="image-modal__mask" onClick={close} />

      <div className="image-modal__container" aria-live="polite">
        {/* 主展示区域 */}
        <div className="image-modal__main">
          {/* 关闭按钮 */}
          <button
            className="image-modal__close"
            onClick={close}
            aria-label="关闭"
          >
            ×
          </button>
          {hasImages ? (
            <div
              className="image-modal__imgbox"
              onClick={handleClick}
              style={{
                overflow: 'auto',
                position: 'relative',
                width: '100%',
                height: '100%',
                cursor: scale === 1 ? 'zoom-in' : 'zoom-out',
              }}
            >
              <img
                key={current}
                className="image-modal__img"
                src={imgsList[current]}
                alt={`预览 ${current + 1} / ${imgsList.length}`}
                draggable={false}
                style={{
                  // transform: `scale(${scale})`,
                  width: '100%', // 放大后宽度为父级容器的100%
                  height: 'auto', // 高度自适应
                  maxHeight: scale === 1 ? '100%' : 'unset',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  e.target.src = onImg;
                }}
              />
            </div>
          ) : (
            <div className="image-modal__empty">暂无图片</div>
          )}
          {/* 页码和按钮区 */}
          {hasImages && (
            <div className="image-modal__footer">
              <span className="image-modal__pager">
                {current + 1} / {imgsList.length}
              </span>
              {imgsList.length > 1 && (
                <>
                  <button
                    className="image-modal__btn"
                    onClick={toPrev}
                    aria-label="上一张"
                  >
                    上一张
                  </button>
                  <button
                    className="image-modal__btn"
                    onClick={toNext}
                    aria-label="下一张"
                  >
                    下一张
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {/* 右侧缩略图区 */}
      {hasImages && (
        <div className="image-modal__thumbs" role="list">
          {imgsList.map((src, idx) => (
            <div
              key={src + idx}
              role="listitem"
              className={`image-modal__thumbwrap ${
                idx === current ? "is-active" : ""
              }`}
              onClick={() => setCurrent(idx)}
              aria-label={`第${idx + 1}张`}
              aria-current={idx === current}
            >
              <div className="image-modal__thumb-bg" />
              <img
                src={src}
                alt={`缩略图 ${idx + 1}`}
                draggable={false}
                className={`image-modal__thumbimg ${
                  idx === current ? "active" : "inactive"
                }`}
                onError={(e) => { e.target.src = onImg }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageModal;
